import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { createControlClient, isApiError } from "@magicweave/api";

import { useSession } from "../auth/context";
import { ClientContext } from "./context";

/**
 * Wires the typed client and the query cache to the session.
 *
 * The retry policy is the part worth reading. TanStack Query's default retries
 * everything three times, which for this API is actively harmful: a 403 will
 * be a 403 on all three attempts, and a 409 from the ledger means the request
 * conflicts with the world's current state, so repeating it unchanged turns
 * one refused write into three. ApiError already knows which failures are
 * about the moment rather than the request, so that judgement lives in one
 * place and is asserted by tests in @magicweave/api.
 */
export function ApiProvider({ baseUrl, children }: { baseUrl: string; children: ReactNode }) {
  const { expire } = useSession();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (attempt, error) => attempt < 2 && isApiError(error) && error.isRetryable,
            // The console shows operational state — balances, what is live in
            // production. Serving it from cache after a minute without asking
            // is how someone reads a stale number and acts on it.
            staleTime: 30_000,
            refetchOnWindowFocus: true,
          },
          mutations: {
            // Never automatically. A mutation that failed may still have been
            // applied, and the idempotency key is the caller's to reuse.
            retry: false,
          },
        },
      }),
  );

  const client = useMemo(
    () => createControlClient({ baseUrl, csrf: true, onUnauthenticated: expire }),
    [baseUrl, expire],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ClientContext value={client}>{children}</ClientContext>
    </QueryClientProvider>
  );
}
